import type { PayloadAction } from '@reduxjs/toolkit';
import {
    createAsyncThunk,
    createSelector,
    createSlice
} from '@reduxjs/toolkit';
import { resetOnLogout } from '~/reducers/common-actions';
import type { RootState } from '~/store';
import type { TaskMetrics } from '~/api/TasksApi';
import tasksApi from '~/api/TasksApi';
import { selectDateOnly } from '~/reducers/selectedDateSlice';
import dateUtils from '~/utils/date-utils';

/**
 * The task metrics state
 */
export type TaskMetricsState = Record<string, TaskMetrics>;

const emptyMetrics: TaskMetricsState = {};

const rejectedMetrics = {
    canceled: 0,
    completed: 0,
    dispatched: 0,
    planned: 0,
    total: 0,
    unassigned: 0
};

export type SetMetricsPayload = {
    date: string;
    metrics: TaskMetrics;
};

export const updateTaskMetrics = createAsyncThunk(
    'taskMetrics/updateTaskMetrics',
    async (selectedDate: string, { getState, fulfillWithValue }) => {
        const routeDate = dateUtils.convertToISODateOnly(selectedDate) || '';
        try {
            const { activeClients = {} } = getState() as RootState;
            const isMultiAccess = Object.keys(activeClients).length > 1;
            const taskMetricsResponse = await tasksApi.getMetrics({
                date: routeDate,
                isMultiAccess
            });
            const taskMetrics = taskMetricsResponse.data.data;
            return fulfillWithValue({ date: routeDate, metrics: taskMetrics });
        } catch (error) {
            return fulfillWithValue({
                date: routeDate,
                metrics: rejectedMetrics
            });
        }
    }
);

export const taskMetricsSlice = createSlice({
    name: 'taskMetrics',
    initialState: emptyMetrics,
    reducers: {
        setTaskMetrics: (
            state: TaskMetricsState,
            action: PayloadAction<Required<SetMetricsPayload>>
        ): TaskMetricsState => {
            const {
                payload: { date: rawDate, metrics }
            } = action;
            const date = dateUtils.convertToISODateOnly(rawDate) || '';

            return date
                ? {
                      ...state,
                      [date]: metrics
                  }
                : state;
        },
        resetTaskMetrics: (): TaskMetricsState => {
            return emptyMetrics;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetOnLogout, () => {
            return emptyMetrics;
        });
        builder.addCase(
            updateTaskMetrics.fulfilled,
            (
                state: TaskMetricsState,
                action: PayloadAction<Required<SetMetricsPayload>>
            ) => {
                const {
                    payload: { date, metrics }
                } = action;
                return date
                    ? {
                          ...state,
                          [date]: metrics
                      }
                    : state;
            }
        );
        builder.addCase(
            updateTaskMetrics.rejected,
            (state: TaskMetricsState) => {
                state = emptyMetrics;
                return state;
            }
        );
    }
});

export const { setTaskMetrics, resetTaskMetrics } = taskMetricsSlice.actions;

/**
 * Returns aggregated task metrics for a range of dates
 */
export const selectTaskMetricsByDates = createSelector(
    [(state: RootState) => state.taskMetrics, (state, dates) => dates],
    (metrics, dates) => {
        return dates.reduce(
            (aggregator: TaskMetrics, date: string) => {
                const taskMetrics = metrics[date];
                if (!taskMetrics) {
                    return aggregator;
                }
                aggregator.total += taskMetrics.total || 0;
                aggregator.unassigned += taskMetrics.unassigned || 0;
                aggregator.planned += taskMetrics.planned || 0;
                aggregator.dispatched += taskMetrics.dispatched || 0;
                aggregator.completed += taskMetrics.completed || 0;
                aggregator.canceled += taskMetrics.canceled || 0;
                return aggregator;
            },
            {
                canceled: 0,
                completed: 0,
                dispatched: 0,
                planned: 0,
                total: 0,
                unassigned: 0
            }
        );
    }
);

/**
 * By default, selects metrics for the current selected date
 */
export const selectTaskMetrics = createSelector(
    [(state) => state, (state) => [selectDateOnly(state)]],
    (state, dates) => {
        return selectTaskMetricsByDates(state, dates);
    }
);

export default taskMetricsSlice.reducer;
